// src/pages/container_rental/components/EstimatePickerDialog.tsx
//
// Modal dialog for picking estimates. Uses the shared EstimateSearchBar
// (with contentType locked to "임대") and emits the picked EstimateDto via onPick().

import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { DataGridPro, GridColDef, GridPaginationModel } from "@mui/x-data-grid-pro";
import { useEstimateApi } from "../../../hooks/useEstimateApi.ts";
import { EstimateDto } from "../../../contracts/estimates/types.ts";
import EstimateSearchBar from "../../../components/common/EstimateSearchBar.tsx";
import {
  EstimateSearchCriteria,
  EstimateSearchResponse,
  defaultCriteria,
  defaultPagination,
} from "../../../contracts/estimates/searchTypes.ts";

interface EstimatePickerDialogProps {
  open: boolean;
  onClose: () => void;
  onPick: (estimate: EstimateDto) => void;
}

interface Row {
  id: string;
  companyInCharge: string;
  estimateDate: string;
  companyName: string;
  siteName: string;
  recipientName: string;
  contentType: string;
  raw: EstimateDto;
}

const LOCKED_CONTENT_TYPE = "임대";

export default function EstimatePickerDialog({
  open,
  onClose,
  onPick,
}: EstimatePickerDialogProps) {
  const { searchEstimates } = useEstimateApi();

  const [criteria, setCriteria] = useState<EstimateSearchCriteria>({
    ...defaultCriteria,
    contentType: LOCKED_CONTENT_TYPE,
  });
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: defaultPagination.pageSize,
  });

  const [response, setResponse] = useState<EstimateSearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    setError(null);
    try {
      const json = await searchEstimates(criteria, {
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        sortField: "estimateDate",
        sortOrder: "desc",
      });
      setResponse(json);
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setLoading(false);
    }
  }, [open, criteria, paginationModel, searchEstimates]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset on open/close
  useEffect(() => {
    if (open) {
      setCriteria({ ...defaultCriteria, contentType: LOCKED_CONTENT_TYPE });
      setPaginationModel({ page: 0, pageSize: defaultPagination.pageSize });
    }
  }, [open]);

  const handleSearch = (newCriteria: EstimateSearchCriteria) => {
    setCriteria({ ...newCriteria, contentType: LOCKED_CONTENT_TYPE });
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  const handleReset = () => {
    setCriteria({ ...defaultCriteria, contentType: LOCKED_CONTENT_TYPE });
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  const rows: Row[] =
    response?.data.map((e) => ({
      id: e.id,
      companyInCharge: e.companyInCharge ?? "",
      estimateDate: e.cover?.estimateDate ?? "",
      companyName: e.cover?.companyName ?? "",
      siteName: e.cover?.siteName ?? "",
      recipientName: e.cover?.recipientName ?? "",
      contentType: e.content?.contentType ?? "",
      raw: e,
    })) ?? [];

  const columns: GridColDef[] = [
    { field: "companyInCharge", headerName: "작성", width: 90 },
    { field: "estimateDate", headerName: "작성일", width: 120 },
    { field: "companyName", headerName: "거래처", flex: 1, minWidth: 140 },
    { field: "siteName", headerName: "현장", flex: 1, minWidth: 140 },
    { field: "recipientName", headerName: "수신", width: 120 },
    { field: "contentType", headerName: "종류", width: 80 },
    {
      field: "actions",
      headerName: "",
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          size="small"
          variant="contained"
          onClick={() => {
            onPick((params.row as Row).raw);
            onClose();
          }}
        >
          선택
        </Button>
      ),
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      aria-labelledby="estimate-picker-title"
    >
      <DialogTitle id="estimate-picker-title">견적서 가져오기</DialogTitle>
      <DialogContent
        dividers
        sx={{ p: 2, height: "70vh", display: "flex", flexDirection: "column" }}
      >
        <EstimateSearchBar
          onSearch={handleSearch}
          onReset={handleReset}
          lockedContentType={LOCKED_CONTENT_TYPE}
        />

        {error && (
          <Typography color="error" variant="body2" sx={{ mb: 1 }}>
            {error}
          </Typography>
        )}

        <Box sx={{ flex: 1, minHeight: 300 }}>
          {loading && rows.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <DataGridPro
              rows={rows}
              columns={columns}
              getRowId={(r) => (r as Row).id}
              loading={loading}
              disableRowSelectionOnClick
              paginationMode="server"
              rowCount={response?.total ?? 0}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[20, 50, 100]}
              pagination
              sx={{ border: "none" }}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>취소</Button>
      </DialogActions>
    </Dialog>
  );
}
